import { GridItem } from "@ui/components/layout/grid/Grid";
import type { BaseHit, Hit } from "instantsearch.js/es/types";
import type { BaseAutocompleteItemProps, QuerySuggestionCollectionValue } from "./types";
import { Text } from "@ui/components/content/text/Text";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { getPagePathByLocale, searchPage } from "@lib/constants/pagePaths";
import { NextLink } from "@ui/components/navigation/link/NextLink";
import { Highlight } from "react-instantsearch";
import type { HistoryItem } from "@algolia/autocomplete-plugin-recent-searches/dist/esm/types";
import { QUERY_SUGGESTIONS_PLUGIN } from "@e2e/helpers/homepage/constants";
import { AllResultsLink } from "./AllResultsLink";
import {
    GRID_AREAS,
    querySuggestionItemLinkHighlightStyles,
    querySuggestionItemLinkStyles,
} from "./AutoCompletePanelStyles";

export interface QuerySuggestionsGridProps
    extends BaseAutocompleteItemProps<QuerySuggestionCollectionValue> {
    onResultClick?: (item: HistoryItem) => void;
}

export const QuerySuggestionsGrid = ({
    autocomplete,
    collection,
    query,
    onResultClick,
}: QuerySuggestionsGridProps) => {
    const { items } = collection || {};
    const { get: getMicrocopy, getMultiple } = useMicrocopy();

    const microcopies = getMultiple(CommonCMS.shared, [
        CommonCMS.searchBarRecentSearches,
        CommonCMS.searchBarPopularSearches,
        CommonCMS.searchBarPanelLabel,
    ]);

    const seeAllText = getMicrocopy(CommonCMS.shared, CommonCMS.search.seeAll, {
        replacements: {
            results: `"${query}"`,
        },
    });
    const { locale } = useRouter();
    const searchPagePath = useMemo(() => {
        return getPagePathByLocale(searchPage, locale);
    }, [locale]);
    return (
        <GridItem
            data-testid={QUERY_SUGGESTIONS_PLUGIN}
            display="flex"
            flexDirection="column"
            gridArea={GRID_AREAS.suggestions}
            {...autocomplete?.getListProps()}
            py={4}
        >
            {!query && (
                <Text
                    tabIndex={0}
                    fontSize="desktopBodyTextSmall"
                    mb="4"
                    fontWeight="bold"
                    textTransform="uppercase"
                    data-testid="autocompleteSearchesHeader"
                >
                    {microcopies[CommonCMS.searchBarPopularSearches]}
                </Text>
            )}
            {items?.map((item) => (
                <NextLink
                    // Highlight can't be applied as an inline properties because it relies on child selectors.
                    sx={querySuggestionItemLinkHighlightStyles}
                    {...querySuggestionItemLinkStyles}
                    href={`${searchPagePath}?query=${encodeURIComponent(item.query)}`}
                    fontSize={query ? "desktopBodyTextSmall" : "desktopHeading5"}
                    key={`${item.objectID}-${item.query}`}
                    onClick={() =>
                        onResultClick?.({
                            id: item.query,
                            label: item.query,
                        })
                    }
                >
                    <Highlight attribute="query" hit={item as any as Hit<BaseHit>} />
                </NextLink>
            ))}
            {query && (
                <AllResultsLink
                    url={`${searchPagePath}?query=${encodeURIComponent(query)}`}
                    label={seeAllText}
                    item={{
                        id: query,
                        label: query,
                    }}
                    onClick={(item) => onResultClick?.(item)}
                />
            )}
        </GridItem>
    );
};
